import { Spine } from '@esotericsoftware/spine-pixi-v8';
import { SpineAdapter } from './SpineAdapter.js';
import { gameConfig } from '../config/gameConfig.js';

/**
 * Real Spine adapter using @esotericsoftware/spine-pixi-v8
 * Drives symbol clips: idle, win, noWin, and reel spin (`spinning` or legacy `blur`).
 */
export class RealSpineAdapter extends SpineAdapter {
  /**
   * @param {import('pixi.js').Container} container
   * @param {Spine} spineObject
   * @param {{
   *   animationController?: import('./AnimationController.js').AnimationController,
   * }} [options]
   */
  constructor(container, spineObject, options = {}) {
    super();
    this.container = container;
    this.spine = spineObject;
    this.animationController = options.animationController ?? null;
    this._currentState = null;
  }

  /**
   * @param {string[]} names
   * @returns {string | null}
   */
  _firstExistingClip(names) {
    const data = this.spine?.skeleton?.data;
    if (!data) return null;
    for (const n of names) {
      if (n && data.findAnimation(n)) return n;
    }
    return null;
  }

  /**
   * @param {string} clip
   */
  _shouldLoopClip(clip) {
    const a = gameConfig.animations;
    if (!clip) return false;
    if (clip === a.idle || clip === 'idle') return true;
    if (clip === a.win || clip === 'win') return true;
    if (clip === a.noWin || clip === 'noWin') return true;
    if (clip === a.spinning || clip === 'spinning') return true;
    if (clip === a.spinningLegacy || clip === 'blur') return true;
    return false;
  }

  /**
   * Play a Spine clip by exact export name (e.g. {@link gameConfig.animations.win}).
   * @param {string} animationName
   */
  play(animationName) {
    if (!this.spine || !this.spine.state || !this.spine.skeleton) return;

    this._currentState = animationName;

    try {
      const state = this.spine.state;
      const clip = this._firstExistingClip([animationName]);

      if (!clip) {
        const idle = this._firstExistingClip([gameConfig.animations.idle, 'idle']);
        if (idle) {
          state.clearTrack(0);
          this.spine.skeleton.setToSetupPose();
          state.setAnimation(0, idle, true);
        } else {
          console.warn(`Animation "${animationName}" not found and no idle clip`);
        }
        return;
      }

      const loop = this._shouldLoopClip(clip);
      // Same hard-reset as setIdle: drop any prior track entry and reset the bones
      // before applying the new clip, so blur bone deformations never bleed through
      // when transitioning to win / noWin / etc.
      state.clearTrack(0);
      this.spine.skeleton.setToSetupPose();
      const entry = state.setAnimation(0, clip, loop);
      if (entry) entry.mixDuration = 0;
    } catch (error) {
      console.error(`Error playing animation "${animationName}":`, error);
    }
  }

  stop() {
    if (!this.spine || !this.spine.state) return;
    try {
      this.spine.state.clearTracks();
    } catch (error) {
      console.error('Error stopping animation:', error);
    }
  }

  setIdle() {
    if (!this.spine || !this.spine.state || !this.spine.skeleton) return;
    const idle = this._firstExistingClip([gameConfig.animations.idle, 'idle']);
    if (!idle) return;
    try {
      // Hard reset path — without this the `blur`/`spinning` clip's bone deformations
      // stay on bones that the `idle` clip does not animate, leaving the symbol
      // visually smeared after the reel has stopped. Sequence:
      //   1. clearTrack(0)      — drop the spinning track entry (no blend lingers).
      //   2. setToSetupPose()   — reset every bone/slot to the default rig pose.
      //   3. setAnimation(idle) — overlay idle on top of the clean setup pose.
      this.spine.state.clearTrack(0);
      this.spine.skeleton.setToSetupPose();
      this._currentState = null;
      const entry = this.spine.state.setAnimation(0, idle, true);
      if (entry) entry.mixDuration = 0;
      this._currentState = idle;
    } catch (e) {
      console.error('RealSpineAdapter.setIdle error:', e);
    }
  }

  /**
   * Reel spin clip: prefers `spinning`, then {@link gameConfig.animations.spinningLegacy} (`blur`).
   */
  setSpinning() {
    if (!this.spine || !this.spine.state) return;
    if (this._currentState === 'spinning') return;
    const a = gameConfig.animations;
    const clip = this._firstExistingClip([
      a.spinning,
      'spinning',
      a.spinningLegacy,
      'blur',
    ]);
    this._currentState = 'spinning';
    if (!clip) {
      this.setIdle();
      return;
    }
    try {
      const state = this.spine.state;
      state.clearTrack(0);
      this.spine.skeleton?.setToSetupPose();
      const entry = state.setAnimation(0, clip, true);
      // Default skeleton mix (idle → blur) can be ~0.2s+. During fast reel scroll the mix
      // never completes, so symbols look idle until deceleration — force an instant cut.
      if (entry) entry.mixDuration = 0;
    } catch (e) {
      console.error('Error playing spin animation:', e);
    }
  }

  /** @deprecated Use {@link setSpinning} */
  setBlur() {
    this.setSpinning();
  }

  setWin() {
    this.play(gameConfig.animations.win);
  }

  /** One full `win` clip (no loop) — used while cycling paylines. */
  setWinOnce() {
    this.playOnce(gameConfig.animations.win);
  }

  /**
   * @param {string} animationName
   */
  playOnce(animationName) {
    if (!this.spine || !this.spine.state) return;

    this._currentState = animationName;

    try {
      const state = this.spine.state;
      const clip = this._firstExistingClip([animationName]);

      if (!clip) {
        this.setIdle();
        return;
      }

      const entry = state.setAnimation(0, clip, false);
      if (entry) entry.mixDuration = 0;
    } catch (error) {
      console.error(`Error playing animation "${animationName}":`, error);
    }
  }

  /**
   * @param {string} animationName
   * @returns {number} seconds, or 0 if missing
   */
  getClipDurationSec(animationName) {
    const clip = this._firstExistingClip([animationName]);
    if (!clip) return 0;
    const anim = this.spine?.skeleton?.data?.findAnimation(clip);
    return anim?.duration > 0 ? anim.duration : 0;
  }

  setNoWin() {
    this.play(gameConfig.animations.noWin);
  }

  reset() {
    this.stop();
    this.setIdle();
  }

  getCurrentState() {
    return this._currentState;
  }
}
